import { GridCell } from './GridCell';
import Maybe from '@Common/Core/Maybe';
import { Grid } from './SimpleGridLight';
import { GridColumn } from './GridColumn';
import constants from '@Common/Core/Constant';
import { GridRow } from './GridRow';
import { GridRender } from './GridRender';

export interface IHotkey {
    code: number;
    alt?: boolean;
    shift?: boolean;
    ctrl?: boolean;
}

export interface ICallback {
    (hotKey: IHotkey, event: JQuery.Event): boolean | void;

    (hotKey: IHotkey): boolean | void;

    (): boolean | void;
}

export class HotkeyManager {
    private static specialKeys: string[] = ['alt', 'ctrl', 'shift'];
    private eventHandlers: { [key: string]: ICallback[] } = {};
    private handler;

    constructor(private root: JQuery, private selector?: string) {
        this.handler = (event: JQuery.Event) => this.processEvent(event);
        if (selector != null) {
            root.on('keydown', selector, this.handler);
        } else {
            root.on('keydown', this.handler);
        }
    }

    public destroy() {
        this.root.off('keydowns', this.selector, this.handler);
        this.eventHandlers = {};
    }

    public on(hotKey: IHotkey, callback: ICallback): void {
        this.getHandlers(hotKey).push(callback);
    }

    public off(hotKey?: IHotkey, callback?: ICallback): void {
        if (hotKey) {
            if (callback) {
                this.getHandlers(hotKey).remove(callback);
            } else {
                this.getHandlers(hotKey).length = 0;
            }
        } else {
            _.forEach(this.eventHandlers, handlers => {
                handlers.length = 0;
            });
        }
    }

    private hotkeyToString(hotkey: IHotkey): string {
        return '' + hotkey.code + HotkeyManager.specialKeys.map((sk: string) => !!hotkey[sk]).join();
    }

    private eventToHotkey(event: JQuery.Event): IHotkey {
        const res: IHotkey = { code: event.keyCode };
        HotkeyManager.specialKeys.forEach(specKey => {
            res[specKey] = !!event[specKey + 'Key'];
        });
        return res;
    }

    private getHandlers(hotkey: IHotkey): ICallback[] {
        const hotkeyString = this.hotkeyToString(hotkey);
        if (this.eventHandlers[hotkeyString]) {
            return this.eventHandlers[hotkeyString];
        }
        return (this.eventHandlers[hotkeyString] = []);
    }

    private processEvent(event: JQuery.Event): boolean {
        const hotkey = this.eventToHotkey(event);
        const handlers = this.getHandlers(hotkey);
        for (let i = 0; i < handlers.length; i++) {
            if (handlers[i](hotkey, event)) {
                return false;
            }
        }
        return true;
    }
}

export class GridNavigationManager {
    private _enabled: boolean = false;
    private manager: HotkeyManager;
    private tableManager: HotkeyManager;
    public newRowFactory: () => void;

    private addRowPending = false;
    private lastEditCell: GridCell;

    constructor(private render: GridRender, private grid: Grid) {
        this.manager = new HotkeyManager(this.render.bodyTable, 'td.simpleGrid-cell');
        this.tableManager = new HotkeyManager(this.render.bodyTable);
        this.render.bodyTable.attr('tabindex', 0);
        this.grid.onEndEdit(cell => (this.lastEditCell = cell));
    }

    private tryForwardMove(): boolean {
        return this.grid.editNextCell();
    }

    private tryBackMove(): boolean {
        return this.grid.editNextCell(false);
    }

    public enabled(enabeled?: boolean): boolean {
        if (arguments.length === 0) {
            return this._enabled;
        }

        this._enabled = enabeled;
        if (this._enabled) {
            this.manager.on({ code: constants.keyCodes.TAB }, () => this.tryForwardMove());
            this.manager.on({ code: constants.keyCodes.TAB, shift: true }, () => this.tryBackMove());
            this.manager.on({ code: constants.keyCodes.ENTER }, () => this.tryAddRow());
            this.tableManager.on({ code: constants.keyCodes.DOWN_ARROW }, () => this.tryMove());
            this.tableManager.on({ code: constants.keyCodes.UP_ARROW }, () => this.tryMove({ up: true }));
        } else {
            this.manager.off();
            this.tableManager.off();
        }
    }

    private getLastEditableColumn(): GridColumn {
        return Maybe(this.grid.getCurrentEditCell() || this.lastEditCell)
            .read(c => c.getColumn())
            .valueOrDefault(null);
    }

    private tryMove({ up = false, column = this.getLastEditableColumn() } = {}) {
        this.grid.endEdit(false);
        this.grid.rowsCollection.selectNextRow({ up });
        this.focusEditableCell(column);
    }

    private focusEditableCell(column?: GridColumn): void {
        const selectedRow = this.grid.rowsCollection.getSelectedRow();
        if (selectedRow == null) {
            return;
        }

        let nextEditCell = column && selectedRow.getCell(column);
        if (!nextEditCell || !nextEditCell.canEdit()) {
            nextEditCell = selectedRow.findNextEditableCell();
        }

        this.grid.editCell(nextEditCell);
        const editor = nextEditCell.getEditor();
        if (editor && editor.focus) {
            editor.focus();
        }
    }

    private tryAddRow(): void {
        if (this.newRowFactory && !this.addRowPending) {
            const selectedRow = this.grid.rowsCollection.getSelectedRow();
            if (this.isLastRow(selectedRow)) {
                this.addRowPending = true;
                this.newRowFactory();
            } else {
                this.tryMove();
            }
        }
    }

    private isLastRow(row: GridRow): boolean {
        return row && row.orderedIndex() === this.grid.rowsCollection.count - 1;
    }

    public rowsChanged() {
        if (this.addRowPending) {
            this.addRowPending = false;
            this.tryMove({ column: null });
        }
    }
}
